import { Button, LinearProgress, TableCell, TableSortLabel, Theme, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
    CustomHeadLabelRenderOptions,
    MUIDataTableColumnOptions,
    MUIDataTableCustomHeadRenderer,
} from 'mui-datatables';
import { FC, ReactNode, useCallback, useMemo } from 'react';

import { AXLE_TEAL, LIGHT_AXLE_TEAL_BACKGROUND } from '../../constants/colors';
import { Children } from '../../utils/reactTypes';
import { HorizontalStack } from '../utils/linearLayouts';

const useStyles = makeStyles((theme: Theme) => ({
    sortLabelRoot: {
        height: '20px',
    },
    sortActive: {
        color: theme.palette.text.primary,
    },
    loadingBarContainer: {
        position: 'relative',
    },
    highlightCell: {
        backgroundColor: LIGHT_AXLE_TEAL_BACKGROUND,
        border: `1px solid ${AXLE_TEAL}`,
    },
}));

type WrapperProps = {
    children: ReactNode;
    isSortable?: boolean;
};

const Wrapper: FC<WrapperProps> = (props) => {
    const { children, isSortable } = props;

    if (isSortable) {
        return (
            <Tooltip title='Sort' placement='bottom'>
                <>{children}</>
            </Tooltip>
        );
    }

    return <>{children}</>;
};

// The (columnMeta, handleToggleColumn, sortOrder) tuple mui-datatables passes to
// a column's customHeadRender, derived from the lib type so it stays in sync.
export type CustomHeadRenderParams = Parameters<
    NonNullable<MUIDataTableColumnOptions['customHeadRender']>
>;

// Head cell for a label-less, icon-only sortable column (e.g. Priority): a
// centered sort control whose arrow is always visible, so the column reads as
// sortable without a text label and still reflects the active sort direction.
export const CenteredSortHeadCell = ({
    columnMeta,
    handleToggleColumn,
    sortOrder,
    label,
}: {
    columnMeta: CustomHeadRenderParams[0];
    handleToggleColumn: CustomHeadRenderParams[1];
    // Widened over the library type: mui-datatables reports a direction of 'none'
    // once a column's sort is cleared, while keeping the column as the sort key.
    sortOrder: Omit<CustomHeadRenderParams[2], 'direction'> & {
        direction: CustomHeadRenderParams[2]['direction'] | 'none' | undefined;
    };
    // Human-readable column name for assistive tech, since the header is label-less.
    label: string;
}) => {
    // A direction of 'none' or undefined means sorting is off, even while this
    // column is still the sort key.
    const sortDirection =
        sortOrder.name === columnMeta.name &&
        (sortOrder.direction === 'asc' || sortOrder.direction === 'desc')
            ? sortOrder.direction
            : undefined;
    const active = sortDirection !== undefined;

    const onClick = useCallback(
        () => handleToggleColumn(columnMeta.index),
        [columnMeta.index, handleToggleColumn],
    );
    const sortLabelSx = useMemo(
        () => ({ '& .MuiTableSortLabel-icon': { opacity: active ? 1 : 0.4 } }),
        [active],
    );

    return (
        <TableCell sx={{ textAlign: 'center' }} aria-label={label}>
            <TableSortLabel
                active={active}
                direction={sortDirection ?? 'asc'}
                onClick={onClick}
                // Keep the arrow visible even when inactive to signal sortability.
                sx={sortLabelSx}
            />
        </TableCell>
    );
};

// Custom head cell component to allow both the typical sorting functionality
// while also allowing custom content in the head cell, to the right of the
// clickable sort icon.
export const CustomTableHeadCell = ({
    children,
    isSortable,
    columnMeta,
    handleToggleColumn,
    sortOrder,
    highlight = false,
    loadingBar = false,
    minWidth,
}: {
    isSortable?: boolean;
    // Key is just here to ensure the caller provides one to the table head cell.
    key: string | number;
    children: Children;
    columnMeta: MUIDataTableCustomHeadRenderer & {
        customHeadLabelRender?:
            | ((options: CustomHeadLabelRenderOptions) => string | ReactNode)
            | undefined;
    };
    handleToggleColumn: (columnIndex: number) => void;
    sortOrder: {
        name: string;
        direction: 'asc' | 'desc' | 'none' | undefined;
    };
    highlight?: boolean;
    loadingBar?: boolean;
    minWidth?: string | number;
}) => {
    const classes = useStyles();
    const sortActive =
        sortOrder.name === columnMeta.name &&
        sortOrder.direction !== 'none' &&
        sortOrder.direction !== undefined;
    const sortLabelProps = {
        classes: { root: classes.sortLabelRoot },
        tabIndex: -1,
        active: sortActive,
        hideSortIcon: true,
        ...(sortOrder.direction !== 'none'
            ? { direction: sortOrder.direction }
            : { direction: undefined }),
    };

    return (
        <TableCell
            classes={{
                root: clsx(
                    highlight ? classes.highlightCell : undefined,
                    loadingBar ? classes.loadingBarContainer : undefined,
                ),
            }}
            sx={{ minWidth }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <Wrapper isSortable={isSortable}>
                    <Button
                        variant='text'
                        sx={{
                            minWidth: 0,
                            paddingX: 1,
                            marginLeft: -1,
                            marginRight: 1,
                            textTransform: 'none',
                            fontWeight: 700,
                            color: (theme) => theme.palette.text.secondary,
                        }}
                        onClick={() => {
                            handleToggleColumn(columnMeta.index);
                        }}
                        data-testid={`headcol-${columnMeta.index}`}
                    >
                        <HorizontalStack>
                            <span>
                                {columnMeta.customHeadLabelRender
                                    ? columnMeta.customHeadLabelRender({
                                          index: columnMeta.index,
                                          colPos: columnMeta.index,
                                          name: columnMeta.name,
                                      })
                                    : columnMeta.label}
                            </span>
                            {isSortable ? (
                                <div>
                                    <TableSortLabel {...sortLabelProps} />
                                </div>
                            ) : null}
                        </HorizontalStack>
                    </Button>
                </Wrapper>
                {children}
                {loadingBar && (
                    <LinearProgress
                        sx={{ height: 2, position: 'absolute', bottom: 0, left: 0, right: 0 }}
                    />
                )}
            </div>
        </TableCell>
    );
};
